import { FC, useState } from 'react';
import { Badge, Button, Col, Modal, Row, Segmented, Tag } from 'antd';
import { capitalCase } from 'change-case';
import { TbArrowRight, TbLayoutGridAdd, TbSquareArrowRight, TbX } from 'react-icons/tb';

import { useEntitlements } from '@/hooks/useEntitlements';
import { LearnMoreFooter } from '@/shared';
import { useInvitationStore } from '@/stores/database/aliases/invitations';
import { SetStateMethod } from '@/types';

import { CreateWorkspace, JoinWorkspace } from './views';

interface JoinOrCreateWorkspaceModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const JoinOrCreateWorkspaceModal: FC<JoinOrCreateWorkspaceModalProps> = ({ open, setOpen }): JSX.Element => {
    // I want to see if all of the user's workspaces are 'inactive' -> then show Workspaces component

    // Check if the property 'status' in each object in workspaces equals 'Inactive'.
    // if so, return true
    // else, return false

    const { hasMinimumPlan } = useEntitlements();
    const { invitations } = useInvitationStore();

    const [segmentedValue, setSegmentedValue] = useState('join');
    const [createFooterState, setCreateFooterState] = useState<{
        disabled: boolean;
        loading: boolean;
        onCreate?: () => void;
    }>({ disabled: true, loading: false });

    return (
        <Modal
            open={open}
            footer={
                <Row align='middle'>
                    <Col flex='none'>
                        <LearnMoreFooter align='left' topic='Workspaces' />
                    </Col>
                    <Col flex='auto' />
                    <Col flex='none' style={{ minHeight: '2rem' }}>
                        {segmentedValue === 'create' && (
                            <Row align='middle' gutter={[8, 0]} wrap={false}>
                                <Col flex='none'>
                                    <Button disabled={createFooterState.loading} onClick={() => setOpen(false)}>
                                        Cancel
                                    </Button>
                                </Col>
                                <Col flex='none'>
                                    <Button
                                        disabled={createFooterState.disabled}
                                        loading={createFooterState.loading}
                                        type='primary'
                                        onClick={createFooterState.onCreate}
                                    >
                                        Create Workspace
                                        <TbArrowRight aria-hidden size='1rem' />
                                    </Button>
                                </Col>
                            </Row>
                        )}
                    </Col>
                </Row>
            }
            title={`${segmentedValue && capitalCase(segmentedValue)} a Workspace`}
            style={{ minWidth: '40rem' }}
            closeIcon={<TbX size='1rem' style={{ verticalAlign: '-0.2rem' }} />}
            onCancel={() => setOpen(false)}
            mask={false}
        >
            <Segmented
                block
                options={[
                    {
                        value: 'join',
                        icon: <TbSquareArrowRight size='1rem' style={{ verticalAlign: '-0.2rem' }} />,
                        label: (
                            <>
                                Join
                                {invitations?.length > 0 && (
                                    <Tag color='blue' style={{ borderColor: 'transparent', marginLeft: '0.5rem' }}>
                                        <Badge dot color='blue' style={{ marginRight: '0.5rem' }} status='processing' />
                                        {invitations?.length}
                                    </Tag>
                                )}
                            </>
                        ),
                    },
                    {
                        value: 'create',
                        icon: <TbLayoutGridAdd size='1rem' style={{ verticalAlign: '-0.2rem' }} />,
                        label: 'Create',
                        disabled: !hasMinimumPlan('plus'),
                    },
                ]}
                value={segmentedValue}
                onChange={(value) => setSegmentedValue(String(value))}
                style={{ marginBottom: '1rem', marginTop: '0.5rem' }}
            />
            <div style={{ position: 'relative' }}>
                <div
                    aria-hidden={segmentedValue !== 'create'}
                    style={{ visibility: segmentedValue === 'create' ? 'visible' : 'hidden' }}
                >
                    <CreateWorkspace onFooterStateChange={setCreateFooterState} />
                </div>
                {segmentedValue === 'join' ? (
                    <div style={{ inset: 0, position: 'absolute' }}>
                        <JoinWorkspace height='100%' setOpen={setOpen} />
                    </div>
                ) : null}
            </div>
        </Modal>
    );
};

export default JoinOrCreateWorkspaceModal;
